

<script setup>

import WorkbenchController from "./editor/WorkbenchController.vue";
import IconSet from "./IconSet.vue";
import { ref, watch, provide, reactive, onMounted, onBeforeUnmount, readonly } from 'vue';
import { autoGenerateDocumentId, createDocumentInStore, db, loadDocumentFromStore, saveDocumentToStore } from "./firestore";
import { call } from "./clients/internalClient";
import { addDoc, collection, deleteDoc, doc, getDoc, onSnapshot, setDoc } from "firebase/firestore";
import { waitFor } from "./utils/waitFor";
import { Events } from "./events";
import { getAuth } from "firebase/auth";
import { Firebase_App } from "./firebase";
import { capturePosthog } from "./posthog";
import { providedRef } from "./composables/common";

const auth = getAuth(Firebase_App);
const user = auth.currentUser

const userInfo = {
  name: user.displayName,
  uid: user.uid,
  email: user.email  
}

const billingFireRef = doc(db, 'billing', user.uid);
const billingTable = await getDoc(billingFireRef);

if (!billingTable.exists()) {
  try {
    await call('POST', '/api/claim-trial');
    capturePosthog('new_user');
  } catch(e) {
    console.error('unable to start trial period', e)
  }
}

const billing = providedRef('billing', null);

onSnapshot(billingFireRef, (snap) => {
  billing.value = snap.exists() ? snap.data() : {};
});

function readHash() {
  let hash = window.location.hash;
  if (hash && hash.length > 0) {
    if (hash.startsWith('#')) {
      hash = hash.substring(1)
    }

    const paymentMatch = /:payment=(.+)$/.exec(hash)
    
    if (paymentMatch) {
      paymentMatch.index
      const payment = paymentMatch[1];
      hash = hash.substring(0, paymentMatch.index);
      window.location.hash = hash
      if (payment === 'success') {
        console.log("payment accepted!")    
      } else if (payment === 'canceled') {
        console.log("payment canceled!")    
      }
    }

    return {
      documentId: hash,
      // any addition navigation like paragraph or comment
    }
    // const [type, id] = hash.split('/');
    // return { type, id };
  }
  return null;
}

async function loadDocument(documentId) {

  const docSnap = await getDoc(doc(db, 'docs', documentId));

  if (docSnap.exists()) {
    return {
      ...docSnap.data(),
      id: documentId
    }
  } else {
    return null;
  }
}

async function storeDocument(document) {
  document.modified = Date.now()
  const {id, ...data} = document
  if (id) {
    await setDoc(doc(db, 'docs', id), data)  
  } else {
    const ref = await addDoc(collection(db, 'docs'), data)
    document.id = ref.id
  }
}

async function saveDocument() {
  if (wizardMode.value) {
    console.warn("ignoring attempt to store a document draft")
    return
  }
  await storeDocument(documentRef.value)
}

function saveDocumentFireAndForget() {
  return saveDocument().then(() => {
    console.log("document saved.")
  }).catch(e => console.error(e));
}

async function deleteDocument(id) {  
  await deleteDoc(doc(db, 'docs', id));
  await deleteDoc(doc(db, 'DocumentContent', id));
  console.log("document deleted")
}

const documentRef = ref(null)
const navigationRef = ref(null)
const blockToFixRef = ref([])
const rejectionDocumentContent = providedRef('rejectionDocumentContent', null)
const wizardMode = providedRef('wizardMode', false)

async function setupAppState() {
  wizardMode.value = false
  
  const searchParams = new URLSearchParams(window.location.search);
  const payment = searchParams?.get('payment')
  // if () {
  //   message.value = 'Order placed! You will receive an email confirmation.';
  // }
  
  let navigation = readHash()
  let document = null;
  if (!navigation) {
    // landing page case  
    document = await loadDocument(userInfo.uid)
    if (!document) {
      document = {
        id: userInfo.uid,
        title: 'Welcome',
        owner: userInfo.uid,
      }
      await storeDocument(document)
    }  
  } else if (navigation.documentId === 'new-document') {

    document = {
      title: 'Untitled',
      owner: userInfo.uid,
    }

    await storeDocument(document)
    capturePosthog('document_created', {
      origin: 'blank'
    })
  } else if (navigation.documentId === 'create') {
    document = {
      id: autoGenerateDocumentId(),
      owner: userInfo.uid,
    }
    wizardMode.value = true    
  } else {
    document = await loadDocument(navigation.documentId)
  }

  if (document && !wizardMode.value) {
    window.location.hash = document.id
    navigation = readHash()
  }

  documentRef.value = document
  navigationRef.value = navigation
}

await setupAppState()

const handleHashChange = () => {
  window.removeEventListener('hashchange', handleHashChange);
  setupAppState().then(() => {
    console.log('application reloaded.')
    window.addEventListener('hashchange', handleHashChange);
  })
};

const replaceNavigationSilently = (hash) => {
  window.removeEventListener('hashchange', handleHashChange);
  window.location.hash = hash
  setTimeout(() => window.addEventListener('hashchange', handleHashChange));
};

function blockToFixChange(e) {
  if (!e.detail) {
    blockToFixRef.value = []
  } else {
    blockToFixRef.value.push(e.detail)
  }
}

provide('document', documentRef);
provide('navigation', navigationRef);
provide('userInfo', userInfo);
provide('blockToFix', blockToFixRef);

provide('appActions', {
  saveDocument: saveDocumentFireAndForget,  
  deleteDocument, replaceNavigationSilently
});

Events.listen(Events.MARK_FOR_FIX, blockToFixChange)
const listenerGroup = Events.group();

onMounted(() => {
  window.addEventListener('hashchange', handleHashChange);
});

onBeforeUnmount(() => {
  window.removeEventListener('hashchange', handleHashChange);
  listenerGroup.dispose()
});

</script>

<template>  
  <WorkbenchController v-if="documentRef" />  
  <div v-else>document doesn't exist</div>
</template>

